import React, { useState } from 'react'
import {
  makeStyles,
  Button,
  ConfirmationDialog,
  DateRangePicker,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DropdownSelect,
  IconButton,
  InputField,
  LinearProgress,
  Avatar,
  Typography,
  useClientOptions,
  useDialogs,
  Tooltip,
  Hidden,
  useUsers,
  CheckIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import type { CalendarEvent } from 'paintscout'
import CalendarTodayIcon from '@material-ui/icons/CalendarToday'
import { getCrews, formatName } from '@paintscout/util/builder'
import moment from 'moment'
import isEqual from 'lodash/isEqual'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { escapeHref } from '@paintscout/util/util'
import { getProjectTypeLabel } from '../../common/ProductionCalendar/util'

const useStyles = makeStyles<Theme, ProductionEventDialogProps>(
  (theme) => ({
    root: {
      overflow: 'visible'
    },
    dialogTitle: {},
    dialogContent: {
      overflow: 'visible',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2)
    },
    cancel: {},
    confirm: {},
    actions: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%'
    },
    infoDiv: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      padding: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    infoDivTextContainer: {
      display: 'flex',
      gap: theme.spacing(1)
    },
    datePicker: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      [theme.breakpoints.down('md')]: {
        alignItems: 'flex-end'
      }
    },
    datePickerIconButton: {
      border: `1px solid ${theme.paintscout.palette.primary}`,
      borderRadius: theme.shape.borderRadius,
      color: theme.paintscout.palette.primary,
      marginBottom: `-${theme.spacing(2.5)}px`,
      height: '40px',
      width: '40px',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.primary,
        color: theme.palette.common.white
      }
    },
    linearProgress: {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
      height: theme.spacing(2),
      borderRadius: '5px'
    },
    linearProgressBar: {
      borderRadius: '5px'
    },
    crewOption: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5)
    },
    crewAvatar: {
      padding: '0px !important'
    },
    crewAvatarIcon: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.common.white,
      display: 'block',
      lineHeight: 1,
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      textRendering: 'geometricPrecision'
    },
    crewOptionText: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: theme.spacing(0.25)
    },
    crewOptionSubtext: {
      fontSize: theme.typography.pxToRem(10)
    }
  }),
  { name: 'ProductionEventDialog' }
)

export interface ProductionEventDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  event: CalendarEvent
  isNew?: boolean
  hideCalendarLink?: boolean
  onCancelEvent: (event: CalendarEvent) => any
  onConfirm: (event: CalendarEvent) => any
}

function ProductionEventDialog(props: ProductionEventDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancelEvent, event, isNew, hideCalendarLink, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const { users } = useUsers()
  const { options } = useClientOptions()

  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()

  const activeCrews = getCrews({ options })
  const allCrews = getCrews({ options, includeTrashed: true })
  const crews = allCrews
  const [currentEvent, setCurrentEvent] = useState<CalendarEvent>(event)
  const [initialEvent] = useState<CalendarEvent>(event)

  const activeCrewOptions = activeCrews.map((crew) => ({
    value: crew._id,
    label: crew.name,
    crewColor: crew.color,
    projectManagerName: crew.projectManager
      ? `${crew.projectManager.firstName} ${crew.projectManager.lastName}`
      : 'No Project Manager'
  }))

  const existingCrew = event.crew ? allCrews.find((c) => c._id === event.crew) : null
  const crewOptions = existingCrew?.trashed
    ? [
        {
          value: existingCrew._id,
          label: `${existingCrew.name} (Deleted)`,
          crewColor: existingCrew.color,
          projectManagerName: existingCrew.projectManager
            ? `${existingCrew.projectManager.firstName} ${existingCrew.projectManager.lastName}`
            : 'No Project Manager',
          disabled: true
        },
        ...activeCrewOptions
      ]
    : activeCrewOptions

  const [currentCrew, setCurrentCrew] = useState(crewOptions.find((c) => c.value === event.crew) || null)

  const handleClose = () => {
    const { previousProgress: _noCompare, ...initialEventWithoutPreviousProgress } = initialEvent
    const { previousProgress: _noCompare2, ...currentEventWithoutPreviousProgress } = currentEvent
    const dirty = !isEqual(initialEventWithoutPreviousProgress, currentEventWithoutPreviousProgress)

    if (!dirty) {
      dismissAllDialogs()
    } else {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. If you exit now, your changes will be lost.',
        onConfirm: () => {
          dismissAllDialogs()
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    }
  }

  const rightContent = (
    <IconButton onClick={handleClose} color="inherit" aria-label="Close">
      <ClearIcon />
    </IconButton>
  )

  return (
    <Dialog {...baseDialogProps} classes={{ paper: classes.root }} maxWidth="sm" fullWidth>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2" weight={'normal'}>
          {isNew ? 'Schedule' : 'Edit'} Job
        </Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <div className={classes.infoDiv}>
          <Typography variant={'h3'} weight={'normal'}>
            {event.title}
          </Typography>
          <Typography variant={'body2'} color="textSecondary">
            {event.address}
          </Typography>
          <div className={classes.infoDivTextContainer}>
            <Typography variant={'subtitle1'}>{getProjectTypeLabel(event.projectType, options)}</Typography>
            <Typography variant={'subtitle1'} component={'span'}>
              •
            </Typography>
            <Typography variant={'subtitle1'}>Est. {event?.deal?.hours || 0} hours</Typography>
          </div>
        </div>
        {!hideCalendarLink && (
          <Hidden smUp>
            <Button
              variant="dialog"
              color="primary"
              fullWidth
              onClick={() => {
                // Example: pass event ID as a query parameter
                const eventId = currentEvent?.id
                window.location.href = escapeHref(
                  `/scheduling/?tab=calendar${eventId && !isNew ? `&eventId=${encodeURIComponent(eventId)}` : ''}`
                )
              }}
            >
              View Calendar
            </Button>
          </Hidden>
        )}

        <div className={classes.datePicker}>
          <DateRangePicker
            hidePresets
            startDate={currentEvent.start ? moment(currentEvent.start) : null}
            endDate={currentEvent.end ? moment(currentEvent.end) : null}
            onConfirm={(startDate, endDate) => {
              setCurrentEvent((prev) => ({ ...prev, start: startDate?.toDate(), end: endDate?.toDate() }))
            }}
            bothDatesRequired
          />
          {!hideCalendarLink && (
            <Hidden lgUp xsDown>
              <Button
                variant="dialog"
                color="primary"
                fullWidth
                onClick={() => {
                  // Example: pass event ID as a query parameter
                  const eventId = currentEvent?.id
                  window.location.href = escapeHref(
                    `/scheduling/?tab=calendar${eventId && !isNew ? `&eventId=${encodeURIComponent(eventId)}` : ''}`
                  )
                }}
              >
                View Calendar
              </Button>
            </Hidden>
          )}
          {!hideCalendarLink && (
            <Hidden mdDown>
              <Tooltip title="View Calendar" placement="right" icon={<></>} enterDelay={300} enterNextDelay={300}>
                <IconButton
                  onClick={() => {
                    const eventId = currentEvent?.id
                    window.location.href = escapeHref(
                      `/scheduling/?tab=calendar${eventId && !isNew ? `&eventId=${encodeURIComponent(eventId)}` : ''}`
                    )
                  }}
                  color="inherit"
                  className={classes.datePickerIconButton}
                  aria-label="View Calendar"
                >
                  <CalendarTodayIcon />
                </IconButton>
              </Tooltip>
            </Hidden>
          )}
        </div>
        <div>
          <DropdownSelect
            options={crewOptions}
            value={currentCrew}
            label="Crew"
            variant="single"
            placeholder={{ selectPlaceholder: 'Select a crew...' }}
            formatOptionLabel={(option, placement) => {
              const selectedCrew = crews.find((crew) => crew._id === option.value)
              const crewAvatarColor = option.crewColor || option.color || selectedCrew?.color
              return (
                <div className={classes.crewOption}>
                  <Avatar
                    className={classes.crewAvatar}
                    disableTooltip
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: crewAvatarColor,
                      color: '#fff',
                      transform: 'none'
                    }}
                  >
                    <i className={`fa-light fa-users ${classes.crewAvatarIcon}`} />
                  </Avatar>
                  <div className={classes.crewOptionText}>
                    <Typography variant="body1">{option.label}</Typography>
                    {placement === 'menu-item' && (
                      <Typography variant="subtitle1" className={classes.crewOptionSubtext}>
                        Project Manager: {option.projectManagerName}
                      </Typography>
                    )}
                  </div>
                </div>
              )
            }}
            onChange={(value) => {
              setCurrentCrew(crewOptions.find((c) => c.value === value.value) || null)
              setCurrentEvent((prev) => ({ ...prev, crew: value ? value.value : null }))
            }}
          />
        </div>
        {!isNew && (
          <>
            <div>
              <InputField
                label="Overall Progress"
                type="number"
                fullWidth
                value={currentEvent.progress ? currentEvent.progress : ''}
                placeholder={'0'}
                defaultValue={0}
                onChange={(e) => {
                  const newProgress = parseInt(e.target.value, 10)
                  setCurrentEvent((prev) => ({
                    ...prev,
                    progress: isNaN(newProgress) ? undefined : Math.min(newProgress, 100),
                    previousProgress: prev.progress
                  }))
                }}
                inputProps={{ min: 0, max: 100 }}
              />
              <LinearProgress
                className={classes.linearProgress}
                classes={{ bar: classes.linearProgressBar }}
                value={currentEvent.progress || 0}
                variant="determinate"
              />
              <Typography variant="subtitle2">{currentEvent.progress || 0}% complete</Typography>
            </div>

            <Button
              variant="eventComplete"
              // className={classes.button}
              active={currentEvent.progress === 100}
              fullWidth
              startIcon={<CheckIcon />}
              onClick={() => {
                if (currentEvent.progress === 100) {
                  setCurrentEvent((prev) => ({
                    ...prev,
                    progress: prev.previousProgress || undefined,
                    previousProgress: prev.progress
                  }))
                } else {
                  setCurrentEvent((prev) => ({ ...prev, progress: 100, previousProgress: prev.progress }))
                }
              }}
            >
              {currentEvent.progress === 100 ? 'Job Complete' : 'Mark Job Complete'}
            </Button>
          </>
        )}
      </DialogContent>
      <DialogActions classes={{ rightButton: isNew ? null : classes.actions }}>
        {!isNew && (
          <Button
            className={classes.cancel}
            variant={'text'}
            onClick={() => onCancelEvent(currentEvent)}
            disabled={isLoading}
            data-testid="production-event-dialog-cancel-button"
          >
            Delete
          </Button>
        )}
        <Button
          className={classes.confirm}
          form="production-event-dialog-form"
          variant={'contained'}
          disabled={isLoading || !currentEvent.crew || !currentEvent.start || !currentEvent.end}
          loading={isLoading}
          data-testid="production-event-dialog-confirm-button"
          onClick={handleSubmit}
        >
          {isNew ? 'Schedule Job' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      const crew = crews.find((c) => c._id === currentEvent.crew)
      const user = users.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(crew?.projectManager?.id))
      const structuredEvent = {
        ...currentEvent,
        progress: currentEvent.progress || 0,
        previousProgress: initialEvent.progress || 0, // previousProgress is used to create the activity, so we need to set it back to the previously saved progress
        estimator:
          currentEvent?.estimator ||
          (user
            ? {
                id: removeAuth0Prefix(user.user_id),
                name: formatName(user.user_metadata),
                email: user.email,
                color: user.user_metadata?.color
              }
            : undefined)
      }
      await onConfirm(structuredEvent)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
      setIsLoading(false)
    }
  }
}

export default ProductionEventDialog
